import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

// project-imports
import { RouteGuard } from 'components/RouteGuard';
import ErrorBoundary from './ErrorBoundary';
import { loader as productsLoader, productLoader } from 'api/products';
import Loadable from 'components/Loadable';
import { SimpleLayoutType } from 'config';
import DashboardLayout from 'layout/Dashboard';
import PagesLayout from 'layout/Pages';
import SimpleLayout from 'layout/Simple';

// render - dashboard
const DashboardDefault = Loadable(lazy(() => import('pages/dashboard/default')));
const DashboardAnalytics = Loadable(lazy(() => import('pages/dashboard/analytics')));
const DashboardFinance = Loadable(lazy(() => import('pages/dashboard/finance')));

// render - widget
const WidgetStatistics = Loadable(lazy(() => import('pages/widget/statistics')));
const WidgetData = Loadable(lazy(() => import('pages/widget/data')));
const WidgetChart = Loadable(lazy(() => import('pages/widget/chart')));

// render - admin panel
const OnlineCoursesDashboard = Loadable(lazy(() => import('pages/admin-panel/online-courses/dashboard/online-dashboard')));
const OnlineCoursesTeacherList = Loadable(lazy(() => import('pages/admin-panel/online-courses/teacher/list')));
const OnlineCoursesAppliedTeacher = Loadable(lazy(() => import('pages/admin-panel/online-courses/teacher/applied')));
const OnlineCoursesAddTeacher = Loadable(lazy(() => import('pages/admin-panel/online-courses/teacher/add')));
const OnlineCoursesStudentList = Loadable(lazy(() => import('pages/admin-panel/online-courses/student/list')));
const OnlineCoursesAppliedStudent = Loadable(lazy(() => import('pages/admin-panel/online-courses/student/applied')));
const OnlineCoursesAddStudent = Loadable(lazy(() => import('pages/admin-panel/online-courses/student/add')));
const OnlineCoursesCoursesView = Loadable(lazy(() => import('pages/admin-panel/online-courses/courses/view')));
const OnlineCoursesAddCourses = Loadable(lazy(() => import('pages/admin-panel/online-courses/courses/add')));
const OnlineCoursesPricing = Loadable(lazy(() => import('pages/admin-panel/online-courses/pricing/online-pricing')));
const OnlineCoursesSite = Loadable(lazy(() => import('pages/admin-panel/online-courses/site/online-site')));
const OnlineCoursesSettingPayment = Loadable(lazy(() => import('pages/admin-panel/online-courses/setting/payment')));
const OnlineCoursesSettingPricing = Loadable(lazy(() => import('pages/admin-panel/online-courses/setting/pricing')));
const OnlineCoursesSettingNotification = Loadable(lazy(() => import('pages/admin-panel/online-courses/setting/notification')));

const AdminMembershipDashbord = Loadable(lazy(() => import('pages/admin-panel/membership/dashboard')));
const AdminMembershipList = Loadable(lazy(() => import('pages/admin-panel/membership/list')));
const AdminMembershipPricing = Loadable(lazy(() => import('pages/admin-panel/membership/pricing')));
const AdminMembershipSetting = Loadable(lazy(() => import('pages/admin-panel/membership/setting')));

const AdminHelpdeskDashbord = Loadable(lazy(() => import('pages/admin-panel/helpdesk/dashboard')));
const AdminHelpdeskCreateTicket = Loadable(lazy(() => import('pages/admin-panel/helpdesk/ticket/create-ticket')));
const AdminHelpdeskTicketList = Loadable(lazy(() => import('pages/admin-panel/helpdesk/ticket/ticket-list')));
const AdminHelpdeskTicketDetails = Loadable(lazy(() => import('pages/admin-panel/helpdesk/ticket/ticket-details')));
const AdminHelpdeskCustomer = Loadable(lazy(() => import('pages/admin-panel/helpdesk/customer')));

// render - applications
const AppChat = Loadable(lazy(() => import('pages/apps/chat')));
const AppCalendar = Loadable(lazy(() => import('pages/apps/calendar')));

const AppKanban = Loadable(lazy(() => import('pages/apps/kanban')));
const AppKanbanBacklogs = Loadable(lazy(() => import('sections/apps/kanban/Backlogs')));
const AppKanbanBoard = Loadable(lazy(() => import('sections/apps/kanban/Board')));

const AppCustomerList = Loadable(lazy(() => import('pages/apps/customer/list')));
const AppCustomerCard = Loadable(lazy(() => import('pages/apps/customer/card')));

const AppInvoiceCreate = Loadable(lazy(() => import('pages/apps/invoice/create')));
const AppInvoiceDashboard = Loadable(lazy(() => import('pages/apps/invoice/dashboard')));
const AppInvoiceList = Loadable(lazy(() => import('pages/apps/invoice/list')));
const AppInvoiceDetails = Loadable(lazy(() => import('pages/apps/invoice/details')));
const AppInvoiceEdit = Loadable(lazy(() => import('pages/apps/invoice/edit')));

const UserProfile = Loadable(lazy(() => import('pages/apps/profiles/user')));
const UserTabPersonal = Loadable(lazy(() => import('sections/apps/profiles/user/TabPersonal')));

const SocialProfile = Loadable(lazy(() => import('pages/apps/profiles/social-profile')));
const SocialTabProfile = Loadable(lazy(() => import('sections/apps/profiles/social-profile/TabProfile')));
const SocialTabFriends = Loadable(lazy(() => import('sections/apps/profiles/social-profile/TabFriends')));
const SocialTabFriendRequest = Loadable(lazy(() => import('sections/apps/profiles/social-profile/TabFriendRequest')));
const SocialTabGallery = Loadable(lazy(() => import('sections/apps/profiles/social-profile/TabGallery')));

const AppECommProducts = Loadable(lazy(() => import('pages/apps/e-commerce/product')));
const AppECommProductDetails = Loadable(lazy(() => import('pages/apps/e-commerce/product-details')));
const AppECommProductList = Loadable(lazy(() => import('pages/apps/e-commerce/products-list')));
const AppECommCheckout = Loadable(lazy(() => import('pages/apps/e-commerce/checkout')));
const AppECommAddProduct = Loadable(lazy(() => import('pages/apps/e-commerce/add-product')));

const AppFileManager = Loadable(lazy(() => import('pages/apps/file-manager')));
const AppMail = Loadable(lazy(() => import('pages/apps/mail')));

// render - forms & tables
const FormsValidation = Loadable(lazy(() => import('pages/forms/validation')));
const FormsWizard = Loadable(lazy(() => import('pages/forms/wizard')));

const FormsLayoutBasic = Loadable(lazy(() => import('pages/forms/layouts/basic')));
const FormsLayoutMultiColumn = Loadable(lazy(() => import('pages/forms/layouts/multi-column')));
const FormsLayoutActionBar = Loadable(lazy(() => import('pages/forms/layouts/action-bar')));
const FormsLayoutStickyBar = Loadable(lazy(() => import('pages/forms/layouts/sticky-bar')));

const FormsPluginsMask = Loadable(lazy(() => import('pages/forms/plugins/mask')));
const FormsPluginsClipboard = Loadable(lazy(() => import('pages/forms/plugins/clipboard')));
const FormsPluginsRecaptcha = Loadable(lazy(() => import('pages/forms/plugins/re-captcha')));
const FormsPluginsEditor = Loadable(lazy(() => import('pages/forms/plugins/editor')));
const FormsPluginsDropzone = Loadable(lazy(() => import('pages/forms/plugins/dropzone')));

const ReactTableBasic = Loadable(lazy(() => import('pages/tables/react-table/basic')));
const ReactDenseTable = Loadable(lazy(() => import('pages/tables/react-table/dense')));
const ReactTableSorting = Loadable(lazy(() => import('pages/tables/react-table/sorting')));
const ReactTableFiltering = Loadable(lazy(() => import('pages/tables/react-table/filtering')));
const ReactTableGrouping = Loadable(lazy(() => import('pages/tables/react-table/grouping')));
const ReactTablePagination = Loadable(lazy(() => import('pages/tables/react-table/pagination')));
const ReactTableRowSelection = Loadable(lazy(() => import('pages/tables/react-table/row-selection')));
const ReactTableExpanding = Loadable(lazy(() => import('pages/tables/react-table/expanding')));
const ReactTableEditable = Loadable(lazy(() => import('pages/tables/react-table/editable')));
const ReactTableDragDrop = Loadable(lazy(() => import('pages/tables/react-table/drag-drop')));
const ReactTableColumnVisibility = Loadable(lazy(() => import('pages/tables/react-table/column-visibility')));
const ReactTableColumnResizing = Loadable(lazy(() => import('pages/tables/react-table/column-resizing')));
const ReactTableStickyTable = Loadable(lazy(() => import('pages/tables/react-table/sticky')));
const ReactTableUmbrella = Loadable(lazy(() => import('pages/tables/react-table/umbrella')));
const ReactTableEmpty = Loadable(lazy(() => import('pages/tables/react-table/empty')));
const ReactTableVirtualized = Loadable(lazy(() => import('pages/tables/react-table/virtualized')));

// render - charts & map
const ChartApexchart = Loadable(lazy(() => import('pages/charts/apexchart')));
const ChartOrganization = Loadable(lazy(() => import('pages/charts/org-chart')));
const Map = Loadable(lazy(() => import('pages/map')));

// table routing
const MuiTableBasic = Loadable(lazy(() => import('pages/tables/mui-table/basic')));
const MuiTableDense = Loadable(lazy(() => import('pages/tables/mui-table/dense')));
const MuiTableEnhanced = Loadable(lazy(() => import('pages/tables/mui-table/enhanced')));
const MuiTableDatatable = Loadable(lazy(() => import('pages/tables/mui-table/datatable')));
const MuiTableCustom = Loadable(lazy(() => import('pages/tables/mui-table/custom')));
const MuiTableFixedHeader = Loadable(lazy(() => import('pages/tables/mui-table/fixed-header')));
const MuiTableCollapse = Loadable(lazy(() => import('pages/tables/mui-table/collapse')));

// pages routing
const AuthLogin = Loadable(lazy(() => import('pages/auth/auth1/login')));
const AuthForgotPassword = Loadable(lazy(() => import('pages/auth/auth1/forgot-password')));
const AuthResetPassword = Loadable(lazy(() => import('pages/auth/auth1/reset-password')));
const AuthCheckMail = Loadable(lazy(() => import('pages/auth/auth1/check-mail')));
const AuthCodeVerification = Loadable(lazy(() => import('pages/auth/auth1/code-verification')));

const AuthLogin2 = Loadable(lazy(() => import('pages/auth/auth2/login2')));
const AuthForgotPassword2 = Loadable(lazy(() => import('pages/auth/auth2/forgot-password2')));
const AuthResetPassword2 = Loadable(lazy(() => import('pages/auth/auth2/reset-password2')));
const AuthCheckMail2 = Loadable(lazy(() => import('pages/auth/auth2/check-mail2')));
const AuthCodeVerification2 = Loadable(lazy(() => import('pages/auth/auth2/code-verification2')));

const AuthLogin3 = Loadable(lazy(() => import('pages/auth/auth3/login3')));

const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/error/404')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/error/500')));
const MaintenanceUnderConstruction = Loadable(lazy(() => import('pages/maintenance/under-construction/under-construction')));
const MaintenanceUnderConstruction2 = Loadable(lazy(() => import('pages/maintenance/under-construction/under-construction2')));
const MaintenanceComingSoon = Loadable(lazy(() => import('pages/maintenance/coming-soon/coming-soon')));
const MaintenanceComingSoon2 = Loadable(lazy(() => import('pages/maintenance/coming-soon/coming-soon2')));

// render - sample page
const SamplePage = Loadable(lazy(() => import('pages/extra-pages/sample-page')));

// render - terceros
const TercerosList = Loadable(lazy(() => import('features/Terceros/components/TercerosList')));
const TercerosForm = Loadable(lazy(() => import('features/Terceros/components/TercerosForm')));
const TercerosView = Loadable(lazy(() => import('features/Terceros/components/TercerosView')));

// render - cuestionarios
const CuestionariosList = Loadable(lazy(() => import('features/Cuestionarios/components/CuestionariosList')));
const CuestionarioCreateInfo = Loadable(lazy(() => import('features/Cuestionarios/components/CuestionarioCreateInfo')));
const CuestionarioCreatePlantilla = Loadable(lazy(() => import('features/Cuestionarios/components/CuestionarioCreatePlantilla')));
const CuestionarioBuilder = Loadable(lazy(() => import('features/Cuestionarios/components/CuestionarioBuilder')));
const CuestionarioDetail = Loadable(lazy(() => import('features/Cuestionarios/components/CuestionarioDetail')));
const CuestionarioVersionHistory = Loadable(lazy(() => import('features/Cuestionarios/components/cuestionario-version-history')));
const EvaluacionesList = Loadable(lazy(() => import('features/Evaluaciones/components/EvaluacionesList')));
const EvaluacionCreateWizard = Loadable(lazy(() => import('features/Evaluaciones/components/EvaluacionCreateWizard')));
const EvaluacionDetalleLayout = Loadable(lazy(() => import('features/Evaluaciones/layout/EvaluacionDetalleLayout')));

// render - usuarios y grupos
const UsuariosLayout = Loadable(lazy(() => import('features/Usuarios/components/UsuariosLayout')));
const UsuariosGruposList = Loadable(lazy(() => import('features/Usuarios/components/GruposList')));
const UsuariosUsuariosList = Loadable(lazy(() => import('features/Usuarios/components/UsuariosList')));
const UsuariosRolesPermisos = Loadable(lazy(() => import('features/Usuarios/components/RolesPermisos')));

// render - settings / auditoria
const SettingsLayout = Loadable(lazy(() => import('features/Settings/components/SettingsLayout')));
const AuditoriaList = Loadable(lazy(() => import('features/Auditoria/components/AuditoriaList')));
const CatalogosList = Loadable(lazy(() => import('features/Catalogos/components/CatalogosList')));

// render - screenings
const ScreeningList = Loadable(lazy(() => import('features/Screening/components/ScreeningList')));
const NewScreeningWizard = Loadable(lazy(() => import('features/Screening/components/new-screening-wizard')));
const ScreeningSearchDetail = Loadable(lazy(() => import('features/Screening/components/screening-search-detail')));
const AnalisisReportesDashboard = Loadable(lazy(() => import('features/analisis-reportes/components/analisis-reportes-dashboard')));
const NotificacionesInbox = Loadable(lazy(() => import('features/notificaciones/components/notificaciones-inbox')));

const Landing = Loadable(lazy(() => import('pages/landing')));
const ContactUS = Loadable(lazy(() => import('pages/contact-us')));
const PricingPage = Loadable(lazy(() => import('pages/extra-pages/price/price1')));
const PricingPage2 = Loadable(lazy(() => import('pages/extra-pages/price/price2')));

// ==============================|| MAIN ROUTES ||============================== //

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: <DashboardLayout />,
      children: [
        {
          path: 'dashboard',
          children: [
            {
              path: 'default',
              element: <DashboardDefault />
            },
            {
              path: 'analytics',
              element: <DashboardAnalytics />
            },
            {
              path: 'finance',
              element: <DashboardFinance />
            }
          ]
        },
        {
          path: 'widget',
          children: [
            {
              path: 'statistics',
              element: <WidgetStatistics />
            },
            {
              path: 'data',
              element: <WidgetData />
            },
            {
              path: 'chart',
              element: <WidgetChart />
            }
          ]
        },
        {
          path: 'admin-panel',
          children: [
            {
              path: 'online-course',
              children: [
                {
                  path: 'dashboard',
                  element: <OnlineCoursesDashboard />
                },
                {
                  path: 'teacher/list',
                  element: <OnlineCoursesTeacherList />
                },
                {
                  path: 'teacher/applied',
                  element: <OnlineCoursesAppliedTeacher />
                },
                {
                  path: 'teacher/add',
                  element: <OnlineCoursesAddTeacher />
                },
                {
                  path: 'student/list',
                  element: <OnlineCoursesStudentList />
                },
                {
                  path: 'student/applied',
                  element: <OnlineCoursesAppliedStudent />
                },
                {
                  path: 'student/add',
                  element: <OnlineCoursesAddStudent />
                },
                {
                  path: 'courses/view',
                  element: <OnlineCoursesCoursesView />
                },
                {
                  path: 'courses/add',
                  element: <OnlineCoursesAddCourses />
                },
                {
                  path: 'pricing',
                  element: <OnlineCoursesPricing />
                },
                {
                  path: 'site',
                  element: <OnlineCoursesSite />
                },
                {
                  path: 'setting/payment',
                  element: <OnlineCoursesSettingPayment />
                },
                {
                  path: 'setting/pricing',
                  element: <OnlineCoursesSettingPricing />
                },
                {
                  path: 'setting/notification',
                  element: <OnlineCoursesSettingNotification />
                }
              ]
            },
            {
              path: 'membership',
              children: [
                {
                  path: 'dashboard',
                  element: <AdminMembershipDashbord />
                },
                {
                  path: 'list',
                  element: <AdminMembershipList />
                },
                {
                  path: 'pricing',
                  element: <AdminMembershipPricing />
                },
                {
                  path: 'setting',
                  element: <AdminMembershipSetting />
                }
              ]
            },
            {
              path: 'helpdesk',
              children: [
                {
                  path: 'dashboard',
                  element: <AdminHelpdeskDashbord />
                },
                {
                  path: 'create-ticket',
                  element: <AdminHelpdeskCreateTicket />
                },
                {
                  path: 'ticket-list',
                  element: <AdminHelpdeskTicketList />
                },
                {
                  path: 'ticket-details',
                  element: <AdminHelpdeskTicketDetails />
                },
                {
                  path: 'customer',
                  element: <AdminHelpdeskCustomer />
                }
              ]
            }
          ]
        },
        {
          path: 'apps',
          children: [
            {
              path: 'chat',
              element: <AppChat />
            },
            {
              path: 'calendar',
              element: <AppCalendar />
            },
            {
              path: 'kanban',
              element: <AppKanban />,
              children: [
                {
                  path: 'backlogs',
                  element: <AppKanbanBacklogs />
                },
                {
                  path: 'board',
                  element: <AppKanbanBoard />
                }
              ]
            },
            {
              path: 'customer',
              children: [
                {
                  path: 'customer-list',
                  element: <AppCustomerList />
                },
                {
                  path: 'customer-card',
                  element: <AppCustomerCard />
                }
              ]
            },
            {
              path: 'invoice',
              children: [
                {
                  path: 'dashboard',
                  element: <AppInvoiceDashboard />
                },
                {
                  path: 'create',
                  element: <AppInvoiceCreate />
                },
                {
                  path: 'details/:id',
                  element: <AppInvoiceDetails />
                },
                {
                  path: 'edit/:id',
                  element: <AppInvoiceEdit />
                },
                {
                  path: 'list',
                  element: <AppInvoiceList />
                }
              ]
            },
            {
              path: 'profiles',
              children: [
                {
                  path: 'user',
                  element: <UserProfile />,
                  children: [
                    {
                      path: 'personal',
                      element: <UserTabPersonal />
                    }
                  ]
                },
                {
                  path: 'social-profile',
                  element: <SocialProfile />,
                  children: [
                    {
                      path: 'profile',
                      element: <SocialTabProfile />
                    },
                    {
                      path: 'friends',
                      element: <SocialTabFriends />
                    },
                    {
                      path: 'friend-requests',
                      element: <SocialTabFriendRequest />
                    },
                    {
                      path: 'gallery',
                      element: <SocialTabGallery />
                    }
                  ]
                }
              ]
            },
            {
              path: 'e-commerce',
              children: [
                {
                  path: 'products',
                  element: <AppECommProducts />,
                  loader: productsLoader,
                  errorElement: <ErrorBoundary />
                },
                {
                  path: 'product-details/:id',
                  element: <AppECommProductDetails />,
                  loader: productLoader,
                  errorElement: <ErrorBoundary />
                },
                {
                  path: 'product-list',
                  element: <AppECommProductList />,
                  loader: productsLoader,
                  errorElement: <ErrorBoundary />
                },
                {
                  path: 'add-new-product',
                  element: <AppECommAddProduct />
                },
                {
                  path: 'checkout',
                  element: <AppECommCheckout />
                }
              ]
            },
            {
              path: 'file-manager',
              element: <AppFileManager />
            },
            {
              path: 'mail',
              element: <AppMail />
            }
          ]
        },
        {
          path: 'forms',
          children: [
            {
              path: 'validation',
              element: <FormsValidation />
            },
            {
              path: 'wizard',
              element: <FormsWizard />
            },
            {
              path: 'layout',
              children: [
                {
                  path: 'basic',
                  element: <FormsLayoutBasic />
                },
                {
                  path: 'multi-column',
                  element: <FormsLayoutMultiColumn />
                },
                {
                  path: 'action-bar',
                  element: <FormsLayoutActionBar />
                },
                {
                  path: 'sticky-bar',
                  element: <FormsLayoutStickyBar />
                }
              ]
            },
            {
              path: 'plugins',
              children: [
                {
                  path: 'mask',
                  element: <FormsPluginsMask />
                },
                {
                  path: 'clipboard',
                  element: <FormsPluginsClipboard />
                },
                {
                  path: 're-captcha',
                  element: <FormsPluginsRecaptcha />
                },
                {
                  path: 'editor',
                  element: <FormsPluginsEditor />
                },
                {
                  path: 'dropzone',
                  element: <FormsPluginsDropzone />
                }
              ]
            }
          ]
        },
        {
          path: 'tables',
          children: [
            {
              path: 'react-table',
              children: [
                {
                  path: 'basic',
                  element: <ReactTableBasic />
                },
                {
                  path: 'dense',
                  element: <ReactDenseTable />
                },
                {
                  path: 'sorting',
                  element: <ReactTableSorting />
                },
                {
                  path: 'filtering',
                  element: <ReactTableFiltering />
                },
                {
                  path: 'grouping',
                  element: <ReactTableGrouping />
                },
                {
                  path: 'pagination',
                  element: <ReactTablePagination />
                },
                {
                  path: 'row-selection',
                  element: <ReactTableRowSelection />
                },
                {
                  path: 'expanding',
                  element: <ReactTableExpanding />
                },
                {
                  path: 'editable',
                  element: <ReactTableEditable />
                },
                {
                  path: 'drag-drop',
                  element: <ReactTableDragDrop />
                },
                {
                  path: 'column-visibility',
                  element: <ReactTableColumnVisibility />
                },
                {
                  path: 'column-resizing',
                  element: <ReactTableColumnResizing />
                },
                {
                  path: 'sticky-table',
                  element: <ReactTableStickyTable />
                },
                {
                  path: 'umbrella',
                  element: <ReactTableUmbrella />
                },
                {
                  path: 'empty',
                  element: <ReactTableEmpty />
                },
                {
                  path: 'virtualized',
                  element: <ReactTableVirtualized />
                }
              ]
            },
            {
              path: 'mui-table',
              children: [
                {
                  path: 'basic',
                  element: <MuiTableBasic />
                },
                {
                  path: 'dense',
                  element: <MuiTableDense />
                },
                {
                  path: 'enhanced',
                  element: <MuiTableEnhanced />
                },
                {
                  path: 'datatable',
                  element: <MuiTableDatatable />
                },
                {
                  path: 'custom',
                  element: <MuiTableCustom />
                },
                {
                  path: 'fixed-header',
                  element: <MuiTableFixedHeader />
                },
                {
                  path: 'collapse',
                  element: <MuiTableCollapse />
                }
              ]
            }
          ]
        },
        {
          path: 'charts',
          children: [
            {
              path: 'apexchart',
              element: <ChartApexchart />
            },
            {
              path: 'org-chart',
              element: <ChartOrganization />
            }
          ]
        },
        {
          path: 'map',
          element: <Map />
        },
        {
          path: 'sample-page',
          element: <SamplePage />
        },
        {
          path: 'terceros',
          children: [
            {
              path: '',
              element: <RouteGuard permission="terceros.ver" redirectTo="/dashboard" />,
              children: [
                {
                  path: '',
                  element: <TercerosList />
                },
                {
                  path: ':id',
                  element: <TercerosView />
                }
              ]
            },
            {
              path: '',
              element: <RouteGuard role="Administrador" redirectTo="/terceros" />,
              children: [
                {
                  path: 'carga-masiva',
                  element: <TercerosForm />
                }
              ]
            },
            {
              path: 'crear',
              element: <Navigate to=".." replace />
            },
            {
              path: ':id/editar',
              element: <Navigate to=".." replace />
            }
          ]
        },
        {
          path: 'cuestionarios',
          children: [
            {
              path: '',
              element: <CuestionariosList />
            },
            {
              path: '',
              element: <RouteGuard permission="cuestionarios.crear_editar" redirectTo="/cuestionarios" />,
              children: [
                {
                  path: 'nuevo',
                  element: <CuestionarioCreateInfo />
                },
                {
                  path: 'nuevo/:id',
                  element: <CuestionarioCreateInfo />
                },
                {
                  path: 'nuevo/responsables',
                  element: <Navigate to="/cuestionarios/nuevo/plantilla" replace />
                },
                {
                  path: 'nuevo/plantilla',
                  element: <CuestionarioCreatePlantilla />
                },
                {
                  path: ':id/editar',
                  element: <CuestionarioBuilder />
                }
              ]
            },
            {
              path: ':id/historial',
              element: <CuestionarioVersionHistory />
            },
            {
              path: ':id',
              element: <CuestionarioDetail />
            }
          ]
        },
        {
          path: 'evaluaciones',
          children: [
            {
              path: '',
              element: <EvaluacionesList />
            },
            {
              path: 'nuevo',
              element: <EvaluacionCreateWizard />
            },
            {
              path: ':id',
              element: <EvaluacionDetalleLayout />
            }
          ]
        },
        {
          path: 'screenings',
          element: <RouteGuard permission="screening.ver_resultados" redirectTo="/terceros" />,
          children: [
            {
              path: '',
              element: <ScreeningList />
            },
            {
              path: '',
              element: <RouteGuard permission="screening.ejecutar" redirectTo="/screenings" />,
              children: [
                {
                  path: 'nuevo',
                  element: <NewScreeningWizard />
                },
                {
                  path: ':id/nueva-busqueda',
                  element: <NewScreeningWizard />
                }
              ]
            },
            {
              path: 'busqueda/:id',
              element: <ScreeningSearchDetail />
            }
          ]
        },
        {
          path: 'analisis-reportes',
          element: <RouteGuard permission="descarga.ver_resumenes" redirectTo="/terceros" />,
          children: [
            {
              path: '',
              element: <AnalisisReportesDashboard />
            }
          ]
        },
        {
          path: 'notificaciones',
          element: <NotificacionesInbox />
        },
        {
          path: 'notificaciones/:id',
          element: <NotificacionesInbox />
        },
        {
          path: 'admin',
          element: <RouteGuard role="Administrador" redirectTo="/terceros" />,
          children: [
            {
              path: 'usuarios',
              element: <UsuariosLayout />,
              children: [
                {
                  path: '',
                  element: <Navigate to="grupos" replace />
                },
                {
                  path: 'grupos',
                  element: <UsuariosGruposList />
                },
                {
                  path: 'usuarios',
                  element: <UsuariosUsuariosList />
                },
                {
                  path: 'roles-permisos',
                  element: <UsuariosRolesPermisos />
                }
              ]
            },
            {
              path: 'usuarios-y-grupos/*',
              element: <Navigate to="/admin/usuarios/grupos" replace />
            },
            {
              path: 'settings',
              element: <SettingsLayout />,
              children: [
                {
                  path: '',
                  element: <Navigate to="auditoria" replace />
                },
                {
                  path: 'auditoria',
                  element: <AuditoriaList />
                },
                {
                  path: 'catalogos',
                  element: <CatalogosList />
                }
              ]
            }
          ]
        },
        {
          path: 'price',
          children: [
            {
              path: 'price1',
              element: <PricingPage />
            },
            {
              path: 'price2',
              element: <PricingPage2 />
            }
          ]
        }
      ]
    },
    {
      path: '/',
      element: <SimpleLayout layout={SimpleLayoutType.LANDING} />,
      children: [
        {
          path: 'landing',
          element: <Landing />
        }
      ]
    },
    {
      path: '/',
      element: <SimpleLayout layout={SimpleLayoutType.SIMPLE} />,
      children: [
        {
          path: 'contact-us',
          element: <ContactUS />
        }
      ]
    },
    {
      path: '/maintenance',
      element: <PagesLayout />,
      children: [
        {
          path: '404',
          element: <MaintenanceError />
        },
        {
          path: '500',
          element: <MaintenanceError500 />
        },
        {
          path: 'under-construction',
          element: <MaintenanceUnderConstruction />
        },
        {
          path: 'under-construction2',
          element: <MaintenanceUnderConstruction2 />
        },
        {
          path: 'coming-soon',
          element: <MaintenanceComingSoon />
        },
        {
          path: 'coming-soon2',
          element: <MaintenanceComingSoon2 />
        }
      ]
    },
    {
      path: '/auth',
      element: <PagesLayout />,
      children: [
        {
          path: 'login',
          element: <AuthLogin />
        },
        {
          path: 'forgot-password',
          element: <AuthForgotPassword />
        },
        {
          path: 'reset-password',
          element: <AuthResetPassword />
        },
        {
          path: 'check-mail',
          element: <AuthCheckMail />
        },
        {
          path: 'code-verification',
          element: <AuthCodeVerification />
        },
        {
          path: 'login2',
          element: <AuthLogin2 />
        },
        {
          path: 'forgot-password2',
          element: <AuthForgotPassword2 />
        },
        {
          path: 'reset-password2',
          element: <AuthResetPassword2 />
        },
        {
          path: 'check-mail2',
          element: <AuthCheckMail2 />
        },
        {
          path: 'code-verification2',
          element: <AuthCodeVerification2 />
        },
        {
          path: 'login3',
          element: <AuthLogin3 />
        }
      ]
    },
    { path: '*', element: <MaintenanceError /> }
  ]
};

export default MainRoutes;
